import { memo } from 'react';
import type { BreedablePokemon } from '../../lib/types';
import { genderSymbol, titleCase } from '../../lib/format';

/**
 * One Pokemon in the Nursery picker.
 *
 * The tray used to draw a sprite, a name, a gender glyph and a genes
 * percentage — four facts, of which two are identical across every Houndour in
 * a collection. The picker had sorts for breeding level, offspring count and
 * size while showing none of the three, which breaks the rule the genes
 * percentage was added for in the first place: *a sort you cannot see the
 * result of is one you take on trust.*
 *
 * So this is built the way `CollectionCard` is — a hierarchy rather than a
 * field dump, and deliberately **not** the same hierarchy. A card in the
 * Collection answers "which of my Pokemon is this"; a card here answers "would
 * this one make a good parent", and those rank the same facts differently.
 * Level leads on a Collection card and is nearly irrelevant here; breeding
 * generation, nature and a breeding record are the reverse.
 *
 * Four lines at most, and the last two only when they have something to say:
 *
 *   1. name
 *   2. gender · level · genetic potential   — always
 *   3. breeding generation · nature         — the two inherited things
 *   4. breeding rank · offspring            — only for a Pokemon that has bred
 *
 * Everything rarer than that is the server's `highlight`, drawn as a single
 * corner mark. Reusing it rather than inventing a picker-specific ranking is
 * what stops a Shiny reading as "Gen 4" here and "Shiny" in the Collection —
 * `pickHighlight` orders them once, rarest first, for both surfaces.
 */

/** "Legendary Parent" is sixteen characters in a hundred-pixel card. */
const shortRank = (rank: string) => rank.split(' ')[0] ?? rank;

/**
 * Breeding generation — *not* Kanto/Johto, which is why it is never labelled
 * just "Gen".
 *
 * A null generation is a Pokemon with no `PokemonLineage` row at all, which is
 * a real and different answer from generation 0: 0 is a founder the game
 * watched arrive, null is one that predates the lineage system. Printing "G0"
 * for both would claim a founder's provenance for a Pokemon nobody recorded,
 * which is the fabrication `migrate:lineage` refuses to make. So it renders as
 * a dash and says so when asked.
 */
function generationLabel(card: BreedablePokemon) {
  if (card.breedGeneration == null) {
    return { text: 'G–', title: 'Breeding generation not recorded — this Pokémon predates the lineage system' };
  }
  const status =
    card.lineageStatus === 'ORIGIN'
      ? 'no parents — this line starts here'
      : card.lineageStatus === 'LEGACY'
        ? 'parents not recorded'
        : `${card.breedGeneration} generation${card.breedGeneration === 1 ? '' : 's'} of breeding`;
  return {
    text: `G${card.breedGeneration}`,
    title: `Breeding generation ${card.breedGeneration} — ${status}`,
  };
}

export const CandidateCard = memo(function CandidateCard({
  card,
  chosen,
  refused,
  disabled,
  onPick,
}: {
  card: BreedablePokemon;
  chosen: boolean;
  /** Cannot pair with the Pokemon already picked. Carries the server's reason. */
  refused: boolean;
  disabled: boolean;
  onPick: (card: BreedablePokemon) => void;
}) {
  const gender = genderSymbol(card.gender);
  const generation = generationLabel(card);
  // `breedingLevel` defaults to **1**, not 0 — so "has a level" is true of
  // every Pokemon in the game and is not the question. Offspring is: the level
  // only rises by breeding, so anything that has never bred is Untested at 1,
  // and a line reading "Untested" under all thirty-six cards in a new
  // trainer's tray is a line nobody reads.
  const hasRecord = card.offspringCount > 0;
  const check = card.compatible;

  return (
    <button
      type="button"
      className={[
        'pxframe',
        'tray-item',
        'cand',
        card.isShiny ? 'pkmn-card--shiny' : '',
        chosen ? 'tray-item--chosen' : '',
        refused ? 'tray-item--refused' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={() => onPick(card)}
      disabled={disabled}
      aria-pressed={chosen}
      // The refusal and the cooldown are answers; the egg groups are the
      // standing question, so they are what the card says when nothing is
      // wrong. Region is here rather than on a line of its own — it is a fact
      // about the species, not about this individual, and the sprite already
      // tells most players which one they are looking at.
      title={
        refused
          ? (check?.reason ?? 'These two cannot breed.')
          : card.onCooldown
            ? 'Still resting after breeding'
            : `${card.speciesName} · ${card.generation === 1 ? 'Kanto' : 'Johto'} · ${card.eggGroups
                .map(titleCase)
                .join(', ')}`
      }
    >
      {/* Top left, so it cannot collide with the different-trainers mark. Icon
          only: the label is a tooltip, because a word here would be wider than
          the card at the size the grid is scanned at. */}
      {card.highlight && (
        <span
          className={`cand__mark cand__mark--${card.highlight.tone}`}
          title={card.highlight.label}
          aria-label={card.highlight.label}
        >
          {card.highlight.icon}
        </span>
      )}

      {/* The gold mark the Bazaar and the check panel both use for a
          different-trainer pair: it improves the shiny odds, so it belongs
          where the choice is made, not only after it. */}
      {check?.ok && check.differentTrainers && (
        <span className="tray-item__flag" title="Different original trainers — better shiny odds">
          ✦
        </span>
      )}

      <img className="pixel" src={card.sprite} alt="" loading="lazy" decoding="async" />
      <span className="tray-item__name">{card.displayName}</span>

      <span className="cand__line">
        <span title={card.gender === 'genderless' ? 'Genderless' : card.gender}>{gender || '—'}</span>
        {' · '}
        <span className="muted">Lv{card.level}</span>
        {' · '}
        {/* The number the "Best genes" sort is ordering by. */}
        <span className="cand__iv" title={`Genetic potential: ${card.potential.label} — ${card.potential.percent}% of a perfect 186 IV total`}>
          {card.potential.percent}%
        </span>
      </span>

      {/* The two things a parent actually passes down. Nature is plain text for
          the reason the Collection gives: 25 nature colours would be 25 things
          to learn for no gain. */}
      <span className="cand__line cand__line--faint">
        <span title={generation.title}>{generation.text}</span>
        {card.natureName && (
          <>
            {' · '}
            <span title={`${card.natureName} nature — inherited by most eggs when a parent has one`}>
              {card.natureName}
            </span>
          </>
        )}
      </span>

      {hasRecord && (
        <span className="cand__line cand__line--faint">
          <span title={`${card.breedingRank} — breeding level ${card.breedingLevel}, which shortens egg production`}>
            {shortRank(card.breedingRank)}
          </span>
          {' · '}
          <span title={`${card.offspringCount} offspring bred`}>{card.offspringCount} bred</span>
        </span>
      )}

      {card.onCooldown && <span className="cand__resting">resting</span>}
    </button>
  );
});
